import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Image as ImageIcon, FilmSlate, Warning } from "@phosphor-icons/react";

/**
 * Loads an attachment as an authenticated blob — no storage URL and no token
 * ever reaches the DOM. Nothing is requested until `media` exists, so a
 * PIN-locked announcement (whose payload has no media at all) can never
 * preload protected content.
 */
export const useAnnouncementMediaUrl = (announcementId, media) => {
    const [url, setUrl] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!media) return;
        let objectUrl = null;
        let cancelled = false;
        setFailed(false);
        setUrl(null);
        api.get(`/announcements/${announcementId}/media`, { responseType: "blob" })
            .then(({ data }) => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(data);
                setUrl(objectUrl);
            })
            .catch(() => !cancelled && setFailed(true));
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [announcementId, media]);

    return { url, failed };
};

/** Inline attachment viewer (admin tracking dialog, compact contexts). */
export const AnnouncementMedia = ({ announcementId, media }) => {
    const { url, failed } = useAnnouncementMediaUrl(announcementId, media);

    if (!media) return null;

    const Icon = media.type === "video" ? FilmSlate : ImageIcon;

    return (
        <div className="mt-4" data-testid="announcement-media">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                <Icon size={13} /> {media.type === "video" ? "Video attachment" : "Photo attachment"}
            </div>
            {failed ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
                    data-testid="announcement-media-error">
                    <Warning size={14} className="inline mr-1 -mt-0.5" />
                    The attachment could not be loaded right now.
                </div>
            ) : !url ? (
                <div className="h-40 rounded-md bg-slate-100 animate-pulse" data-testid="announcement-media-loading" />
            ) : media.type === "video" ? (
                <video src={url} controls playsInline
                    className="w-full max-h-72 rounded-md border border-[#E2E8F0] bg-black"
                    data-testid="announcement-media-video" />
            ) : (
                <img src={url} alt={media.original_filename || "Attachment"}
                    className="w-full max-h-72 object-contain rounded-md border border-[#E2E8F0] bg-slate-50"
                    data-testid="announcement-media-image" />
            )}
        </div>
    );
};
